import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import EditIcon from '@mui/icons-material/Edit';

import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { SpeedDial } from '../../components/SpeedDial';
import {
  PATH_ORGANIZATION_CONTACT,
  PATH_ORGANIZATION_CONTACT_EDIT,
} from '../../app/constants/paths.constant';
import { RightDrawer } from '../../components/RightDrawer';
import { FormVariant } from '../../forms';
import { ThirdPartyFormTab } from '../../forms/ThirdParty/enums/tab.enum';
import { OrganizationForm } from '../../forms/ThirdParty/Organization';
import { ContactInfo as ContactInfoCmp } from '../../components/ContactInfo';
import { useBuildPaths } from '../../hooks/useBuildPaths.hook';

export const OrganizationPage_ContactInfo_Organization_Fragment = graphql(`
  fragment OrganizationPage_ContactInfo_Organization_Fragment on Organization {
    _id
    isInternal
    contactInfo {
      email
      mobileNumber
      landlineNumber
      professionalNumber
      faxNumber
      website
      ...ContactInfoCmp_ContactInfo_Fragment
    }
    ...ThirdPartyForm_Organization_Fragment
  }
`);

interface Props {
  readonly organization: FragmentType<
    typeof OrganizationPage_ContactInfo_Organization_Fragment
  >;
}

export const ContactInfo = ({
  organization: pOrganization,
}: Readonly<Props>) => {
  const organization = getFragmentData(
    OrganizationPage_ContactInfo_Organization_Fragment,
    pOrganization,
  );

  const navigate = useNavigate();

  const { paths, currentPathKey } = useBuildPaths(
    {
      consult: PATH_ORGANIZATION_CONTACT,
      edit: PATH_ORGANIZATION_CONTACT_EDIT,
    },
    { id: organization._id },
  );

  const isInEditionMode = currentPathKey === 'edit';
  const leaveEditionMode = useCallback(
    () => void navigate(paths.consult),
    [navigate, paths.consult],
  );

  return (
    <>
      <ContactInfoCmp contactInfo={organization.contactInfo} />
      <SpeedDial
        actions={[
          {
            id: 'editOrganizationContactInfo',
            icon: <EditIcon />,
            name: 'Modifier Infos de Contact',
            to: paths.edit,
          },
        ]}
        title="Actions possibles sur les Informations de Contact de l'Organisation"
      />
      <RightDrawer isOpen={isInEditionMode} onDrawerClose={leaveEditionMode}>
        <OrganizationForm
          formVariant={FormVariant.Modification}
          initialValues={organization}
          isInternal={organization.isInternal}
          onCancel={leaveEditionMode}
          onSuccess={leaveEditionMode}
          organizationId={organization._id}
          stepIdsSubset={[ThirdPartyFormTab.ContactInfo]}
          title="Formulaire de modification des Informations de Contact de l'Organisation"
        />
      </RightDrawer>
    </>
  );
};
